<template>
  <div class="automation-wrap">
    <page-header :title="creating ? `Create Automation (${selectedShow.title})` : 'Automation'">
      <template #right>
        <div class="actions">
          <b-dropdown v-if="!creating"
                      :disabled="loading"
                      :text="selectedShow ? `Show: ${selectedShow.title || ''}` : 'Select Show'"
                      no-flip>
            <div style="max-height: 250px; overflow-y: scroll">
              <b-dropdown-item key="all" @click="selectShow()">
                All shows
              </b-dropdown-item>
              <b-dropdown-item :key="show.id"
                               @click="selectShow(show)"
                               v-for="show in unfilteredShows">
                {{ show.title }}
              </b-dropdown-item>
            </div>
          </b-dropdown>
          <b-button v-if="!creating && selectedShow && !loading"
                    @click="isVacationModalVisible = true">{{ vacationMode ? 'Edit' : 'Create' }} Vacation</b-button>
          <b-button variant="primary"
                    v-if="!creating"
                    :disabled="!selectedShow || loading"
                    @click="createNewAutomation">New Automation</b-button>
          <b-modal centered v-model="isVacationModalVisible" ref="vacation-modal" hide-footer :title="`${vacationMode ? 'Edit' : 'Create'} Vacation`">
            <div style="display: flex; gap: 25px; margin-bottom: 25px;">
              <div style="width: 185px">
                Start Date
                <b-dropdown ref="calendarDropdown" no-caret class="post-calendar-button">
                  <template #button-content>
                    <div class="calendar-button-content">
                <span>
                  {{ vacationStartDate }}
                </span>
                      <span>
                  <b-icon-calendar4-week />
                </span>
                    </div>
                  </template>
                  <b-calendar v-model="vacationStartDate" />
                </b-dropdown>
              </div>
              <div style="width: 185px">
                End Date
                <b-dropdown ref="calendarDropdown" no-caret class="post-calendar-button">
                  <template #button-content>
                    <div class="calendar-button-content">
                <span>
                  {{ vacationEndDate }}
                </span>
                      <span>
                  <b-icon-calendar4-week />
                </span>
                    </div>
                  </template>
                  <b-calendar v-model="vacationEndDate" />
                </b-dropdown>
              </div>
            </div>
            <div style="display: flex; gap: 15px;">
              <b-button variant="primary"
                        :disabled="vacationStartDate === 'mm/dd/yyyy' || vacationEndDate === 'mm/dd/yyyy'"
                        @click="saveVacation">Save</b-button>
              <b-button class="btn btn-danger" @click="deleteVacation" v-if="vacationMode">Delete</b-button>
            </div>
          </b-modal>
        </div>
      </template>
    </page-header>
    <div class="loader" v-if="loading">
      <loading-spinner />
      <span>Loading...</span>
    </div>
    <div v-else class="table">
      <div v-if="creating">
        <new-automation />
      </div>
      <b-table v-else
        tbody-tr-class="post-table-row"
        thead-tr-class="post-table-head"
        :borderless="true"
        dark="dark"
        :items="filteredAutomations"
        :responsive="true"
        :sticky-header="true"
        :fields="fields"
      >
        <template #cell(show_id)="data">
          {{ getShow(data.item.show_id) }}
        </template>
        <template #cell(actions)="data">
          <three-dots-menu :items="[{
            text: 'Edit',
            onClick: () => editAutomation(data.item)
          },
          {
            text: 'Delete',
            color: '#F55858',
            onClick: () => {
              showDeleteAutomation(data.item.id);
            }
          }]" />
        </template>
      </b-table>
      <confirm-modal-v-3 v-if="isConfirmDeleteModalOpen"
                         contentText="Are you sure you want to delete this automation?"
                         confirmText="Delete"
                         cancelText="Cancel"
                         :is-danger="true"
                         :loading="deleting"
                         loading-text="Deleting..."
                         @close="isConfirmDeleteModalOpen = false"
                         @confirm="deleteAutomation"
      />
    </div>
  </div>
</template>

<script>

import { defineComponent } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import PageHeader from '@/components/PageHeader.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ThreeDotsMenu from '@/components/ThreeDotsMenu.vue';
import NewAutomation from '@/views/automation/NewAutomation.vue';
import ConfirmModalV3 from '@/components/ConfirmationModal.vue';
import { BIconCalendar4Week } from 'bootstrap-vue';

export default defineComponent({
  components: {
    BIconCalendar4Week,
    ConfirmModalV3,
    NewAutomation,
    PageHeader,
    ThreeDotsMenu,
    LoadingSpinner
  },

  data() {
    return {
      loading: false,
      creating: false,
      deleting: false,
      vacation: false,
      selectedShow: null,
      vacationMode: false,
      automationId: null,
      vacationId: null,
      vacationEndDate: 'mm/dd/yyyy',
      vacationStartDate: 'mm/dd/yyyy',
      isVacationModalVisible: false,
      isConfirmDeleteModalOpen: false,
      fields: [
        { key: 'show_id', label: 'Show', thStyle: { width: '100px' } },
        { key: 'title', label: 'Title', thStyle: { width: '100px' } },
        { key: 'pattern', label: 'Pattern', thStyle: { width: '100px' }, },
        { key: 'day_of_week', label: 'Day(s) of Week', thStyle: { width: '100px' }, },
        { key: 'start_time', label: 'Start Time', thStyle: { width: '100px' }, },
        { key: 'end_time', label: 'End Time', thStyle: { width: '70px' }, },
        { key: 'start_date', label: 'Start Date', thStyle: { width: '100px' }, },
        { key: 'end_date', label: 'End Date', thStyle: { width: '70px' }, },
        { key: 'actions', label: 'Action', thStyle: { width: '70px' }, },
      ]
    };
  },

  computed: {
    ...mapGetters(['showSchedules']),
    ...mapState(['station']),
    ...mapGetters(['unfilteredShows', 'activeShow']),

    filteredAutomations() {
      return this.showSchedules.filter((automation) => {
        return !this.selectedShow || automation.show_id === this.selectedShow.id;
      });
    },
  },

  methods: {
    ...mapActions(['fetchSchedulesForShow', 'createVacation', 'updateVacation', 'deleteSchedule', 'removeVacation']),

    async saveVacation() {
      const showId = this.selectedShow.id.toString();
      if (this.vacationMode) {
        this.updateVacation([this.vacationId, showId, this.vacationStartDate, this.vacationEndDate]);
        this.isVacationModalVisible = false;
      } else {
        const newVacation = await this.createVacation([showId, this.vacationStartDate, this.vacationEndDate]);
        this.vacationId = newVacation.id;
        this.vacationMode = true;
        this.isVacationModalVisible = false;
      }
    },

    deleteVacation() {
      this.removeVacation(this.vacationId);
      this.refreshSchedules(this.selectedShow.id);
      this.vacationMode = false;
      this.isVacationModalVisible = false;
    },

    getShow(showId) {
      const show = (this.unfilteredShows || []).find((show) => show.id === showId);
      return show ? show.title : '';
    },

    createNewAutomation() {
      this.$router.push({
        path: `/${this.station}/automations/new?showId=${this.selectedShow.id}`
      });
    },

    editAutomation(automation) {
      this.$router.push({
        path: `/${this.station}/automations/${automation.id}/edit?showId=${automation.show_id}`,
      });
    },

    async selectShow(show, navigate = true) {
      if (!show) {
        this.selectedShow = null;
        this.$store.dispatch('setActiveShow', -1);
        navigate && this.$router.push({ query: { ...this.$route.query, showId: undefined } });
      } else {
        this.selectedShow = show;
        navigate && this.$router.push({ query: { ...this.$route.query, showId: show.id } });
        this.$store.dispatch('setActiveShow', show.id);
        this.$store.dispatch('fetchShowData', show.id);
        await this.$store.dispatch('fetchSchedulesForShow');
        this.refreshSchedules(show.id);
      }
    },

    showDeleteAutomation(automationId) {
      this.automationId = automationId;
      this.isConfirmDeleteModalOpen = true;
    },

    async deleteAutomation() {
      try {
        this.deleting = true;
        await this.deleteSchedule(this.automationId);
        await this.$store.dispatch('fetchSchedulesForShow');
        this.automationId = null;
        this.isConfirmDeleteModalOpen = false;
      } catch (e) {
        console.error(e);
      } finally {
        this.deleting = false;
      }
    },

    async refreshSchedules(showId) {
      this.tableData = [];

      this.showSchedules.forEach(element => {
        if (element['show_id'] !== showId) return;
        this.tableData.push(element);
      });

      this.tableData[0].holidays.forEach(entry => {
        if (entry.active) {
          this.vacationStartDate = entry.first_day;
          this.vacationEndDate = entry.last_day;
          this.vacationId = entry.id;
          this.vacationMode = entry.active;
        }
      });
    },
  },

  async created() {
    this.loading = true;
    await this.$store.dispatch('fetchAllShowsData');
    await this.$store.dispatch('getSocialMediaConnections');
    await this.$store.dispatch('fetchSchedulesForShow');
    this.loading = false;
  },

  mounted() {
    if (this.selectedShow) { this.refreshSchedules(this.selectedShow.id); }
  },

  watch: {
    unfilteredShows: function () {
      const showId = this.$route.query.showId;
      if (!showId) return;
      const show = this.unfilteredShows.find((show) => show.id === Number(showId));
      if (show) {
        this.selectShow(show, false);
      }
    }
  }
});
</script>

<style lang="scss" scoped>

.automation-wrap {
  display: flex;
  padding: 25px;
  flex-direction: column;
  justify-content: center;
}

.actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.loader {
  color: white;
  margin: 40px auto;
}
</style>
